import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

// Backend URL used for top-level navigation to /api/auth/{provider}/login
// (must be a real navigation, not an XHR — provider needs to issue 302).
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

function DiscordIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3a.075.075 0 0 0-.079.037c-.34.601-.717 1.386-.98 2.005a18.27 18.27 0 0 0-5.487 0A12.683 12.683 0 0 0 9.018 3.037.077.077 0 0 0 8.939 3 19.736 19.736 0 0 0 5.18 4.369a.07.07 0 0 0-.032.027C2.526 8.046 1.81 11.629 2.16 15.17a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.993 3.03.077.077 0 0 0 .084-.027c.462-.63.873-1.295 1.226-1.994a.076.076 0 0 0-.041-.105 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.127c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127c-.598.349-1.22.642-1.873.891a.077.077 0 0 0-.041.106c.36.698.772 1.363 1.226 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-4.094-.838-7.648-3.549-10.776a.061.061 0 0 0-.031-.028zM8.02 13.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.175 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

function XIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function GoogleIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" />
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" />
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z" />
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" />
    </svg>
  );
}

// Spotify "wordmark-less" official mark (the green circle with three sound bars).
// Single-color via `currentColor` so it inherits whatever text color we set
// on the button \u2014 we color it `#1DB954` (Spotify brand green) inline below.
function SpotifyIcon({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.301.421-1.02.599-1.561.3z" />
    </svg>
  );
}

/**
 * Social login button row. Hides providers that aren't configured on the backend
 * (reads /api/auth/providers on mount).
 */
export default function SocialLoginButtons({ mode = 'login' }) {
  const [providers, setProviders] = useState(null);

  useEffect(() => {
    let active = true;
    api
      .get('/auth/providers')
      .then((r) => {
        if (active) setProviders(r.data || {});
      })
      .catch(() => {
        if (active) setProviders({});
      });
    return () => {
      active = false;
    };
  }, []);

  const startOAuth = (provider) => {
    // Full-page navigation — provider needs to issue 302 redirects.
    const redirect = '/crate';
    window.location.href = `${BACKEND_BASE_URL}/api/auth/${provider}/login?redirect=${encodeURIComponent(redirect)}`;
  };

  if (providers === null) return null; // loading silent

  const anyEnabled = providers.discord || providers.x || providers.google || providers.spotify;
  if (!anyEnabled) return null;

  const verb = mode === 'signup' ? 'Sign up' : 'Continue';

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-2">
        {providers.google && (
          <Button
            type="button"
            variant="outline"
            onClick={() => startOAuth('google')}
            className="w-full rounded-xl border-border hover:border-foreground/40 hover:bg-foreground/5"
            data-testid="oauth-google-button"
          >
            <GoogleIcon className="w-4 h-4 mr-2" />
            {verb} with Google
          </Button>
        )}
        {providers.spotify && (
          <Button
            type="button"
            variant="outline"
            onClick={() => startOAuth('spotify')}
            className="w-full rounded-xl border-[#1DB954]/40 hover:border-[#1DB954] hover:bg-[#1DB954]/10"
            data-testid="oauth-spotify-button"
          >
            <SpotifyIcon className="w-4 h-4 mr-2 text-[#1DB954]" />
            {verb} with Spotify
          </Button>
        )}
        {providers.discord && (
          <Button
            type="button"
            variant="outline"
            onClick={() => startOAuth('discord')}
            className="w-full rounded-xl border-[#5865F2]/40 hover:border-[#5865F2] hover:bg-[#5865F2]/10"
            data-testid="oauth-discord-button"
          >
            <DiscordIcon className="w-4 h-4 mr-2 text-[#5865F2]" />
            {verb} with Discord
          </Button>
        )}
        {providers.x && (
          <Button
            type="button"
            variant="outline"
            onClick={() => startOAuth('x')}
            className="w-full rounded-xl border-foreground/20 hover:border-foreground/50 hover:bg-foreground/5"
            data-testid="oauth-x-button"
          >
            <XIcon className="w-4 h-4 mr-2" />
            {verb} with X
          </Button>
        )}
      </div>
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider text-muted-foreground/60 font-mono">
        <div className="h-px flex-1 bg-border" />
        <span>or</span>
        <div className="h-px flex-1 bg-border" />
      </div>
    </div>
  );
}
